import React, { useEffect, useMemo, useState } from 'react'
import { Accordion, Form, Row, Col } from 'react-bootstrap'
import classNames from 'classnames'
import Button from '@components/common/Button/Button.component'
import {
  isPerioSection,
  PerioChartSectionData,
  PerioSection,
} from '@dataTypes/chartingFeature.types'
import {
  getProbeSiteLabels,
  getToothIndex,
  toDisplayOrder,
  toStorageOrder,
} from '@utils/perio.utils'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { useActivePerioChart } from '@hooks/useActivePerioChart'
import { getFullName } from '@utils/fullname.util'
import {
  CHART_PROVIDER_INVALID_ERROR,
  isChartProviderHardError,
} from '@utils/chart-validation.utils'
import {
  buildProviderSearchList,
  resolveChartProviderDisplayName,
} from '@utils/provider-options.util'
import {
  setCreatePerioChart,
  setPerioUpdated,
} from '@store/reducers/charting.reducer'
import { ComboBoxComponent } from '@syncfusion/ej2-react-dropdowns'
import ChartNameComboBox from '@components/pages/Charting/ChartNameComboBox.component'

interface PerioSidePanelProps {
  voiceStatus?: React.ReactNode
  /** When true (mic connecting/listening/processing), open Voice transcription. */
  voiceActive?: boolean
  /** Bumped when manual save fails on Charting information fields. */
  chartingInfoFocusTick?: number
  voiceMeasurement?: React.ReactNode
  selectedTooth: number
  setSelectedTooth: (tooth: number) => void
  selectedSection: PerioSection
  setSelectedSection: (section: PerioSection) => void
  validationErrors: { [key: string]: string }
  updatePerioValue: (
    section: PerioSection,
    label: keyof PerioChartSectionData,
    toothIndex: number,
    newValue: string,
  ) => void
  setValidationErrors: React.Dispatch<
    React.SetStateAction<{
      [key: string]: string
    }>
  >
}

const CHARTING_INFO_ACCORDION_KEY = '0'
const VOICE_TRANSCRIPTION_ACCORDION_KEY = '2'

const toAccordionKey = (
  eventKey: string | string[] | null | undefined,
): string => {
  if (typeof eventKey === 'string') {
    return eventKey
  }
  return ''
}

const PerioSidePanel = ({
  voiceStatus,
  voiceActive = false,
  chartingInfoFocusTick = 0,
  voiceMeasurement,
  selectedTooth,
  setSelectedTooth,
  selectedSection,
  setSelectedSection,
  updatePerioValue,
  validationErrors,
  setValidationErrors,
}: PerioSidePanelProps): React.ReactElement => {
  const dispatch = useAppDispatch()
  const { activePerioChart, isExplicitHistorySelection, isPersistedChart } =
    useActivePerioChart()
  const createPerioChart = useAppSelector(
    (state) => state.charting.createPerioChart,
  )
  const selectedPerioChart = useAppSelector(
    (state) => state.charting.selectedPerioChart,
  )
  const selectedPatient = useAppSelector(
    (state) => state.patient.selectedPatient,
  )
  const providers = useAppSelector((state) => state.provider.data)
  const selectedClinicId = useAppSelector(
    (state) => state.clinic.selectedClinic?.clinic_id,
  )
  const isPerioLocked = useAppSelector((state) => state.charting.isPerioLocked)
  const isViewMode = isExplicitHistorySelection
  const providerSearchList = useMemo(
    () => buildProviderSearchList(providers, selectedClinicId),
    [providers, selectedClinicId],
  )

  const [provId, setProvId] = useState('')
  const [chartName, setChartName] = useState('')
  const [activeAccordionKey, setActiveAccordionKey] = useState<string>(
    CHARTING_INFO_ACCORDION_KEY,
  )

  useEffect(() => {
    if (!voiceActive) {
      return
    }
    setActiveAccordionKey(VOICE_TRANSCRIPTION_ACCORDION_KEY)
  }, [voiceActive])

  useEffect(() => {
    if (chartingInfoFocusTick <= 0) {
      return
    }
    setActiveAccordionKey(CHARTING_INFO_ACCORDION_KEY)
    const frame = window.requestAnimationFrame(() => {
      document
        .querySelector(
          '.perio-side-panel .accordion-item[data-charting-info="true"]',
        )
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' })
    })
    return (): void => {
      window.cancelAnimationFrame(frame)
    }
  }, [chartingInfoFocusTick])

  const toothIndex = getToothIndex(selectedTooth)

  const getFieldValue = (label: keyof PerioChartSectionData): string => {
    const raw = activePerioChart.perio_data[selectedSection][label][toothIndex]
    if (label === 'Mobility') return raw?.split(' ')[0] ?? '0'
    return raw ?? '0 0 0'
  }

  const validateSingleDigit = (inputValue: string): string => {
    // Remove all non-digit characters
    const digitsOnly = inputValue.replace(/\D/g, '')

    // If no digits found or empty, return '0'
    if (!digitsOnly || digitsOnly === '') {
      return '0'
    }

    // Return only the first digit (in case multiple digits were pasted)
    return digitsOnly.charAt(0)
  }

  const renderTripleInput = (
    label: keyof PerioChartSectionData,
  ): React.ReactElement[] => {
    const storageParts = getFieldValue(label).split(' ')
    if (label === 'Mobility') {
      return [
        <Form.Control
          key="mobility"
          className="text-center perio-triplet-input"
          value={storageParts[0]}
          onChange={(e) => {
            const validatedValue = validateSingleDigit(e.target.value)
            updatePerioValue(selectedSection, label, toothIndex, validatedValue)
          }}
          onFocus={(e) => {
            // Select all text when input is focused/clicked
            e.target.select()
          }}
          onClick={(e) => {
            // Select all text when input is clicked
            e.currentTarget.select()
          }}
          onKeyDown={(e) => {
            // If a digit is pressed, select all text first so it gets replaced
            if (/^[0-9]$/.test(e.key)) {
              const target = e.target as HTMLInputElement
              target.select()
              return
            }
            // Allow navigation keys, backspace, delete
            if (
              e.key === 'ArrowLeft' ||
              e.key === 'ArrowRight' ||
              e.key === 'Backspace' ||
              e.key === 'Delete' ||
              e.key === 'Tab' ||
              e.key === 'Enter'
            ) {
              return
            }
            // Prevent all other keys
            e.preventDefault()
          }}
          readOnly={isPerioLocked || isViewMode}
        />,
      ]
    }
    const displayParts = toDisplayOrder(selectedTooth, storageParts)
    return displayParts.map((val, i) => (
      <Form.Control
        key={i}
        className="text-center perio-triplet-input"
        value={val}
        onChange={(e) => {
          const validatedValue = validateSingleDigit(e.target.value)
          const nextDisplay = [...displayParts]
          nextDisplay[i] = validatedValue
          updatePerioValue(
            selectedSection,
            label,
            toothIndex,
            toStorageOrder(selectedTooth, nextDisplay).join(' '),
          )
        }}
        onFocus={(e) => {
          // Select all text when input is focused/clicked
          e.target.select()
        }}
        onClick={(e) => {
          // Select all text when input is clicked
          e.currentTarget.select()
        }}
        onKeyDown={(e) => {
          // If a digit is pressed, select all text first so it gets replaced
          if (/^[0-9]$/.test(e.key)) {
            const target = e.target as HTMLInputElement
            target.select()
            return
          }
          // Allow navigation keys, backspace, delete
          if (
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight' ||
            e.key === 'Backspace' ||
            e.key === 'Delete' ||
            e.key === 'Tab' ||
            e.key === 'Enter'
          ) {
            return
          }
          // Prevent all other keys
          e.preventDefault()
        }}
        readOnly={isPerioLocked || isViewMode}
      />
    ))
  }

  const handleToggleDBMSurface = (
    label: 'Bleeding' | 'Calculus' | 'Supp' | 'Furcation',
    surface: 'D' | 'B' | 'M',
  ): void => {
    const current = getFieldValue(label).split(' ')
    const surfaceSet = new Set(current)
    if (surfaceSet.has(surface)) {
      surfaceSet.delete(surface)
    } else {
      surfaceSet.add(surface)
    }
    const updated = ['D', 'B', 'M']
      .map((s) => (surfaceSet.has(s) ? s : '0'))
      .join(' ')
    updatePerioValue(selectedSection, label, toothIndex, updated)
  }

  const renderDBMButtons = (
    label: 'Bleeding' | 'Calculus' | 'Supp' | 'Furcation',
  ): React.ReactElement[] => {
    const currentValueSet = new Set(getFieldValue(label).split(' '))

    return getProbeSiteLabels(selectedTooth).map((surface) => {
      const isSelected = currentValueSet.has(surface)
      return (
        <Button
          key={surface}
          size="sm"
          aria-pressed={isSelected}
          className={classNames('button-secondary', 'perio-dbm-buttons', {
            active: isSelected,
          })}
          onClick={() => handleToggleDBMSurface(label, surface)}
          disabled={isPerioLocked || isViewMode}
        >
          {surface}
        </Button>
      )
    })
  }

  const getSingleValue = (label: 'Mobility'): string => {
    const raw =
      activePerioChart?.perio_data?.[selectedSection]?.[label]?.[toothIndex]
    return raw?.split(' ')[0] || '0'
  }

  const updateSingleValue = (label: 'Mobility', value: string): void => {
    const sanitized = value.split(' ')[0]
    updatePerioValue(selectedSection, label, toothIndex, sanitized)
  }

  const isNewPerioChart =
    !createPerioChart?.chart_id?.trim() && !isPersistedChart

  const hasChartingValidationErrors = (): boolean => {
    return !!(
      (validationErrors.patient_id &&
        !selectedPatient &&
        !selectedPerioChart) ||
      (isChartProviderHardError(validationErrors.provider_id) &&
        isNewPerioChart) ||
      (validationErrors.title && isNewPerioChart)
    )
  }

  return (
    <div className="perio-side-panel">
      <Accordion
        activeKey={activeAccordionKey}
        onSelect={(eventKey) => {
          setActiveAccordionKey(toAccordionKey(eventKey))
        }}
        flush
      >
        <Accordion.Item eventKey="0" data-charting-info="true">
          <Accordion.Header
            className={
              hasChartingValidationErrors() ? 'invalid border-danger' : ''
            }
          >
            Charting information
          </Accordion.Header>
          <Accordion.Body className="p-0">
            <Form.Group className="mb-2">
              <Form.Label className="small">Patient name</Form.Label>
              <Form.Control
                type="text"
                size="sm"
                isInvalid={
                  !!validationErrors.patient_id &&
                  !selectedPatient &&
                  !selectedPerioChart
                }
                placeholder="Patient name"
                value={
                  getFullName(selectedPerioChart?.patient ?? selectedPatient) ??
                  'No Selected Patient'
                }
                readOnly
              />
              {validationErrors.patient_id && (
                <span className="error-text ps-2">
                  {validationErrors.patient_id}
                </span>
              )}
            </Form.Group>
            <Form.Group className="mb-2 position-relative">
              <Form.Label className="small">Provider name</Form.Label>
              {!isPersistedChart && !isViewMode ? (
                <>
                  <ComboBoxComponent
                    dataSource={providerSearchList}
                    allowCustom={true}
                    allowFiltering={true}
                    filterType="Contains"
                    fields={{ text: 'name', value: 'id' }}
                    placeholder="Provider name"
                    value={
                      createPerioChart
                        ? createPerioChart.provider_id
                        : (provId ?? '')
                    }
                    change={(e) => {
                      if (e.isInteracted === false) {
                        return
                      }
                      const selectedValue = e.value
                      const previousProviderId =
                        createPerioChart?.provider_id ?? ''
                      const nextProviderId =
                        selectedValue === undefined || selectedValue === null
                          ? ''
                          : String(selectedValue)
                      if (nextProviderId === previousProviderId) {
                        return
                      }
                      const isValidProvider = providerSearchList
                        .map((p) => p.id)
                        .includes(selectedValue)
                      setProvId(e.value)
                      dispatch(
                        setCreatePerioChart({
                          ...createPerioChart,
                          provider_id: e.value,
                        }),
                      )
                      if (e.value !== undefined) {
                        setValidationErrors((prevErrors) => {
                          const updatedErrors = { ...prevErrors }
                          const hasProviderSelection =
                            selectedValue !== undefined &&
                            selectedValue !== null &&
                            String(selectedValue).trim() !== ''

                          if (!hasProviderSelection) {
                            delete updatedErrors['provider_id']
                          } else if (isValidProvider) {
                            delete updatedErrors['provider_id']
                          } else {
                            updatedErrors['provider_id'] =
                              CHART_PROVIDER_INVALID_ERROR
                          }
                          return updatedErrors
                        })
                        dispatch(setPerioUpdated(true))
                      }
                    }}
                    disabled={isPerioLocked}
                    cssClass={`charting-combobox ${isChartProviderHardError(validationErrors.provider_id) && isNewPerioChart ? ' border-danger' : ''} ${isPerioLocked ? 'disabled pe-none' : ''}`}
                  />
                  {isChartProviderHardError(validationErrors.provider_id) &&
                    isNewPerioChart && (
                      <span className="error-text ps-2">
                        {validationErrors.provider_id}
                      </span>
                    )}
                </>
              ) : (
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Provider name"
                  value={resolveChartProviderDisplayName(
                    activePerioChart?.provider,
                    activePerioChart?.provider_id,
                    providerSearchList,
                  )}
                  readOnly
                />
              )}
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label className="small">Chart name</Form.Label>
              {!isPersistedChart && !isViewMode ? (
                <>
                  <ChartNameComboBox
                    value={
                      createPerioChart
                        ? createPerioChart.title
                        : (chartName ?? '')
                    }
                    readOnly={isPerioLocked}
                    disabled={isPerioLocked}
                    isInvalid={!!validationErrors.title && isNewPerioChart}
                    onChange={(nextTitle) => {
                      setChartName(nextTitle)
                      dispatch(
                        setCreatePerioChart({
                          ...createPerioChart,
                          title: nextTitle,
                        }),
                      )
                      if (nextTitle.trim()) {
                        setValidationErrors((prevErrors) => {
                          const updatedErrors = { ...prevErrors }
                          delete updatedErrors['title']
                          return updatedErrors
                        })
                      }
                      dispatch(setPerioUpdated(true))
                    }}
                  />
                  {validationErrors.title && isNewPerioChart && (
                    <span className="error-text ps-2">
                      {validationErrors.title}
                    </span>
                  )}
                </>
              ) : (
                <Form.Control
                  type="text"
                  size="sm"
                  placeholder="Chart name"
                  value={activePerioChart.title ?? ''}
                  readOnly
                />
              )}
            </Form.Group>
          </Accordion.Body>
        </Accordion.Item>

        <Accordion.Item eventKey="1">
          <Accordion.Header>Treatment shortcuts</Accordion.Header>
          <Accordion.Body className="p-0">
            <Row className="mb-2 align-items-center gx-1">
              <Col xs="4" className="form-row-label">
                Area
              </Col>
              <Col xs="8" className="form-row-control">
                <Form.Select
                  size="sm"
                  value={selectedSection}
                  onChange={(e) => {
                    if (isPerioSection(e.target.value)) {
                      setSelectedSection(e.target.value)
                    }
                  }}
                  disabled={isPerioLocked}
                  className={`${isPerioLocked ? 'disabled pe-none' : ''} input-container`}
                >
                  <option value={PerioSection.Facial}>Facial</option>
                  <option value={PerioSection.Lingual}>Lingual</option>
                </Form.Select>
              </Col>
            </Row>

            <Row className="mb-2 align-items-center gx-1">
              <Col xs="4" className="form-row-label">
                Tooth no.
              </Col>
              <Col xs="8" className="form-row-control">
                <div className="counter-wrapper d-flex align-items-center justify-content-between w-100 input-container">
                  <Button
                    size="sm"
                    className="button-secondary border-0"
                    onClick={() =>
                      setSelectedTooth(Math.max(1, selectedTooth - 1))
                    }
                    disabled={isPerioLocked}
                  >
                    -
                  </Button>
                  <input
                    value={selectedTooth}
                    onChange={(e) => {
                      const val = Number(e.target.value)
                      if (val >= 1 && val <= 32) {
                        setSelectedTooth(val)
                      }
                    }}
                    min={1}
                    max={32}
                    className={`form-control text-center border-0 ${isPerioLocked ? 'disabled pe-none' : ''}`}
                    style={{ width: '2.5rem', height: '1.75rem', padding: 0 }}
                    readOnly={isPerioLocked}
                  />
                  <Button
                    size="sm"
                    className="button-secondary border-0"
                    onClick={() =>
                      setSelectedTooth(Math.min(32, selectedTooth + 1))
                    }
                    disabled={isPerioLocked}
                  >
                    +
                  </Button>
                </div>
              </Col>
            </Row>

            {voiceMeasurement && (
              <Row className="mb-2 align-items-center gx-1">
                <Col
                  as="label"
                  htmlFor="perio-voice-measurement"
                  xs="4"
                  className="form-row-label"
                >
                  Voice
                </Col>
                <Col xs="8" className="form-row-control">
                  {voiceMeasurement}
                </Col>
              </Row>
            )}

            <Row className="align-items-center mb-2 gx-1">
              <Col xs="4" className="form-row-label" />
              <Col xs="8" className="form-row-control dbm-labels">
                <div className="input-container d-flex justify-content-center align-items-center gap-2 w-100">
                  {getProbeSiteLabels(selectedTooth).map((site) => (
                    <span key={site}>{site}</span>
                  ))}
                </div>
              </Col>
            </Row>

            <hr className="mb-2 mt-1" />

            {(['PD', 'GM', 'CAL'] as const).map((label) => (
              <Row key={label} className="mb-1 align-items-center gx-1">
                <Col xs="4" className="form-row-label">
                  {label}
                </Col>
                <Col
                  xs="8"
                  className="form-row-control d-flex justify-content-center align-items-center gap-1"
                >
                  {renderTripleInput(label)}
                </Col>
              </Row>
            ))}

            {(['Bleeding', 'Calculus', 'Supp', 'Furcation'] as const).map(
              (label) => (
                <Row
                  key={label}
                  className="mb-1 align-items-center gx-1 dbm-buttons-container"
                >
                  <Col xs="4" className="form-row-label">
                    {label}
                  </Col>
                  <Col
                    xs="8"
                    className="form-row-control d-flex justify-content-center align-items-center gap-1"
                  >
                    {renderDBMButtons(label)}
                  </Col>
                </Row>
              ),
            )}

            <Row className="mb-1 align-items-center gx-1">
              <Col xs="4" className="form-row-label">
                Mobility
              </Col>
              <Col
                xs="8"
                className="form-row-control d-flex justify-content-between align-items-center"
              >
                <div className="counter-wrapper perio-mobility-container">
                  <Button
                    size="sm"
                    className="button-secondary border-0"
                    onClick={() => {
                      const current =
                        parseInt(getSingleValue('Mobility'), 10) || 0
                      updateSingleValue(
                        'Mobility',
                        String(Math.max(0, current - 1)),
                      )
                    }}
                    disabled={isPerioLocked || isViewMode}
                  >
                    -
                  </Button>
                  <input
                    min={0}
                    max={9}
                    value={getSingleValue('Mobility')}
                    onChange={(e) => {
                      const val = Number(e.target.value)
                      if (val >= 0 && val <= 9) {
                        updateSingleValue('Mobility', String(val))
                      }
                    }}
                    className="form-control text-center border-0"
                    style={{ width: '2.5rem', height: '1.75rem', padding: 0 }}
                    readOnly={isPerioLocked || isViewMode}
                  />
                  <Button
                    size="sm"
                    className="button-secondary border-0"
                    onClick={() => {
                      const current =
                        parseInt(getSingleValue('Mobility'), 10) || 0
                      updateSingleValue(
                        'Mobility',
                        String(Math.min(9, current + 1)),
                      )
                    }}
                    disabled={isPerioLocked || isViewMode}
                  >
                    +
                  </Button>
                </div>
              </Col>
            </Row>
          </Accordion.Body>
        </Accordion.Item>
        {voiceStatus && (
          <Accordion.Item eventKey="2">
            <Accordion.Header>Voice transcription</Accordion.Header>
            <Accordion.Body className="p-0">{voiceStatus}</Accordion.Body>
          </Accordion.Item>
        )}
      </Accordion>
    </div>
  )
}

export default PerioSidePanel
